import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./Login.scss";

import instance from "../../api/apiClient";

import { setIsAuthorized, getUserInfo } from "../../store/user/slice";
import { useAppDispatch } from "../../store/index";
import ROUTES from "../../router/routes";
import { apiRoutes } from "../../configs/apiRoutes";

import Input from "../../components/inputs/Input/Input";
import Checkbox from "../../components/inputs/Checkbox/Checkbox";
import Button from "../../components/buttons/Button";
import eyeIcon from "../../assets/icons/eye.svg";
import eyeOffIcon from "../../assets/icons/eye-off.svg";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import Notifications from "components/global/Notifications/Notifications";
import { currentBrand } from "../../configs/brands";
import { handleAuthSuccess } from "helpers/handleAuthSuccess";
import { getBusinessErrorCodes } from "helpers/handleApiError";

const isUserBlockedError = (error: unknown) =>
  getBusinessErrorCodes(error).includes("userBlocked");

const Login: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [mail, setMail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [twoFaStep, setTwoFaStep] = useState<
    "login" | "setup2fa" | "verify2fa"
  >("login");
  const [twoFaTempToken, setTwoFaTempToken] = useState<string | null>(null);
  const [twoFaQrDataUrl, setTwoFaQrDataUrl] = useState<string | null>(null);
  const [isReturningToLogin, setIsReturningToLogin] = useState(false);

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const sessionExpired = localStorage.getItem("sessionExpired");

    if (sessionExpired) {
      dispatch(
        showNotification({
          title: "Warning",
          message: t("sessionExpired"),
          type: NotificationTypes.warning,
        }),
      );

      localStorage.removeItem("sessionExpired");
    }
  }, [dispatch]);

  const handleLogIn = async () => {
    setIsFetching(true);
    setHasError(false);

    try {
      const payload: Record<string, any> = {
        email: mail,
        password: password,
      };

      if (currentBrand.is2FA) {
        payload.twoFa = 1;
      }

      const res = await instance.post(apiRoutes.login, payload);
      const data = res.data;

      if (data.twoFaStatus === "setup_required") {
        setTwoFaTempToken(data.twoFaTempToken);
        setTwoFaQrDataUrl(data.twoFaQrDataUrl);
        setIsReturningToLogin(false);
        setTwoFaStep("setup2fa");
        return;
      }

      if (data.twoFaStatus === "code_required") {
        setTwoFaTempToken(data.twoFaTempToken);
        setIsReturningToLogin(false);
        setTwoFaStep("verify2fa");
        return;
      }

      if (data.accessToken) {
        handleAuthSuccess(data.accessToken, dispatch, navigate);
        return;
      }

      setHasError(true);
      dispatch(
        showNotification({
          title: t("loginFailed"),
          message: t("unexpectedServerResponse"),
          type: NotificationTypes.error,
        }),
      );
    } catch (error) {
      setHasError(true);
      const isBlocked = isUserBlockedError(error);
      dispatch(
        showNotification({
          title: isBlocked ? t("accountBlocked") : t("loginFailed"),
          message: isBlocked
            ? t("blockedUserContactAdmin")
            : t("invalidCredentials"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setIsFetching(false);
    }
  };

  const confirmTwoFa = async (fullCode: string) => {
    try {
      const res = await instance.post(apiRoutes.confirm2fa, {
        code: fullCode,
        tempToken: twoFaTempToken,
      });

      const token = res?.data?.accessToken;
      if (!token) {
        dispatch(
          showNotification({
            title: t("verificationFailed"),
            message: t("invalid2fa"),
            type: NotificationTypes.error,
          }),
        );
        setHasError(true);
        return;
      }

      localStorage.setItem("token", token);
      dispatch(setIsAuthorized(true));
      navigate(ROUTES.DASHBOARD);
      dispatch(getUserInfo());
    } catch (error) {
      const isBlocked = isUserBlockedError(error);
      dispatch(
        showNotification({
          title: isBlocked ? t("accountBlocked") : t("verificationFailed"),
          message: isBlocked ? t("blockedUserContactAdmin") : t("invalid2fa"),
          type: NotificationTypes.error,
        }),
      );
      setHasError(true);
      setCode(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    }
  };

  const handleCodeChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...code];
    updated[index] = value;
    setCode(updated);

    if (value && index < 5) inputsRef.current[index + 1]?.focus();
    if (!value && index > 0) inputsRef.current[index - 1]?.focus();

    if (updated.join("").length === 6) {
      confirmTwoFa(updated.join(""));
    }
  };

  const handleBackToLogin = () => {
    setIsReturningToLogin(true);
    setCode(["", "", "", "", "", ""]);
    setTwoFaTempToken(null);
    setTwoFaQrDataUrl(null);
    setHasError(false);
    setTwoFaStep("login");
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace") {
      const updated = [...code];

      if (updated[index]) {
        updated[index] = "";
        setCode(updated);
        return;
      }

      e.preventDefault();

      if (index > 0) {
        updated[index - 1] = "";
        setCode(updated);
        inputsRef.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-content">
        <div className="login-content-logo-wrapper">
          <img
            className="login-content-logo"
            src={currentBrand?.logoUrl}
            alt={currentBrand?.title}
          />
          <p className="login-content-logo-description">{t("poweredBy")}</p>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={twoFaStep}
            className="login-step"
            custom={isReturningToLogin}
            variants={{
              initial: (isReturning) => ({
                opacity: 0,
                x: isReturning ? -24 : 24,
              }),
              animate: { opacity: 1, x: 0 },
              exit: (isReturning) => ({
                opacity: 0,
                x: isReturning ? 24 : -24,
              }),
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            onAnimationComplete={() => {
              if (twoFaStep !== "login") {
                inputsRef.current[0]?.focus();
              }
            }}
          >
            {twoFaStep === "login" && (
              <form className="login-content--form" autoComplete="on">
                <Input
                  hasError={hasError}
                  value={mail}
                  onChange={(e) => {
                    setHasError(false);
                    setMail(e);
                  }}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  label={t("emailAddress")}
                  onAutoFill={(value) => {
                    setHasError(false);
                    setMail(value);
                  }}
                />

                <Input
                  hasError={hasError}
                  value={password}
                  onChange={(e) => {
                    setHasError(false);
                    setPassword(e);
                  }}
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  label={t("password")}
                  icon={showPassword ? eyeOffIcon : eyeIcon}
                  iconPosition="right"
                  onIconClick={() => setShowPassword((prev) => !prev)}
                  onAutoFill={(value) => {
                    setHasError(false);
                    setPassword(value);
                  }}
                />

                <div className="login-content--form--bottom">
                  <Checkbox
                    value={remember}
                    txt={t("rememberPassword")}
                    onCheck={(e) => setRemember(e)}
                    id="remember-password"
                  />

                  <Button
                    txt={t("signIn")}
                    type="submit"
                    handleClick={handleLogIn}
                    disabled={isFetching || !mail || !password}
                    isLoad={isFetching}
                  />
                </div>
              </form>
            )}

            {twoFaStep === "setup2fa" && (
              <div className="login-2fa-wrapper">
                <p className="login-2fa-text">{t("scanQr")}</p>

                <img className="login-2fa-qr" src={twoFaQrDataUrl!} alt="qr" />

                <p className="login-2fa-text">{t("enterCode")}</p>

                <div className="twofa-inputs">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      ref={(el) => (inputsRef.current[index] = el)}
                      onChange={(e) => handleCodeChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="twofa-input"
                    />
                  ))}
                </div>
              </div>
            )}

            {twoFaStep === "verify2fa" && (
              <div className="login-2fa-wrapper login-2fa-wrapper--verify">
                <p className="login-2fa-text">{t("enterCodeVerify")}</p>

                <div className="twofa-inputs">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      ref={(el) => (inputsRef.current[index] = el)}
                      onChange={(e) => handleCodeChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="twofa-input"
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>
          {twoFaStep !== "login" && (
            <motion.button
              className="login-back-button"
              type="button"
              onClick={handleBackToLogin}
              aria-label="Back to sign in"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 0.72, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <span aria-hidden="true">←</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <Notifications />
    </div>
  );
};

export default Login;
